import React from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { device, useBreakpoints } from "../lib/breakpoints";
import Button from "../components/Button";
import SEO from "../components/SEO";
import { APP_NAME, TAGLINE } from "../helpers/constants";
import { marketingSectionHeading } from "../styles/marketingSectionTypography";
import { trackEvent } from "../helpers/mixpanel";
import Logo from "../components/general/Logo";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  HomeOutlined,
  MessageOutlined,
  ToolOutlined,
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  ClockCircleOutlined,
  SettingOutlined,
  ThunderboltOutlined,
  FileTextOutlined,
  RocketOutlined,
  SafetyOutlined,
  BarChartOutlined,
  HeartOutlined,
  StarOutlined,
  ArrowRightOutlined,
  UserAddOutlined,
  BulbOutlined,
} from "@ant-design/icons";

/** Page-specific hero (global HERO_* tokens target the homepage). */
const PAGE_HERO_EYEBROW = "CLUMSE · PROCUREMENT OS";
const PAGE_HERO_LEAD =
  "Clumse helps you scope work, run vendor outreach, compare bids, and keep dispatch organized, without losing control of approvals or spend.";

const ForOperators = () => {
  const navigate = useNavigate();
  const { isMobile } = useBreakpoints();

  const handleBookWalkthrough = () => {
    trackEvent("Self Managing Landlords - Book Walkthrough Click", {
      page: "/for-operators",
      section: "cta",
      device_type: isMobile ? "mobile" : "desktop",
    });
    navigate("/contact");
  };

  return (
    <Container>
      <SEO
        title={`For Operators | ${APP_NAME} · Vendor bids & dispatch`}
        description={`Clumse is bid-and-repair software: scoped jobs, vendor outreach, bid comparison, and Clumse AI in the dashboard. Built for teams that want tighter quotes and less inbox chaos.`}
        keywords={`vendor bids, maintenance bid search, Clumse AI, dispatch software, facilities operators`}
        imageAlt={`${APP_NAME} for operators and maintenance teams`}
        url={`https://www.clumse.com/for-operators`}
        canonical={`https://www.clumse.com/for-operators`}
      />

      {/* Hero Section */}
      <HeroSection>
        <HeroBackgroundPattern />
        <HeroContent>
          <HeroIconWrapper>
            <Logo color="white" displayMode="icon" size="large" linkTo={null} />
          </HeroIconWrapper>
          <HeroEyebrow>{PAGE_HERO_EYEBROW}</HeroEyebrow>
          <HeroTitle>{TAGLINE}</HeroTitle>
          <HeroLead>{PAGE_HERO_LEAD}</HeroLead>
          <HeroCTA>
            <HeroCTAButton onClick={handleBookWalkthrough}>
              See How It Works
              <ArrowRightOutlined />
            </HeroCTAButton>
          </HeroCTA>
        </HeroContent>
      </HeroSection>

      {/* Statistics Section */}
      <StatsSection>
        <ContentContainer>
          <StatsGrid>
            <StatCard>
              <StatIcon>
                <ThunderboltOutlined />
              </StatIcon>
              <StatNumber>24/7</StatNumber>
              <StatLabel>AI Response Time</StatLabel>
              <StatDescription>
                Requests get structured fast, even when they arrive after hours
              </StatDescription>
            </StatCard>
            <StatCard>
              <StatIcon>
                <MessageOutlined />
              </StatIcon>
              <StatNumber>80%</StatNumber>
              <StatLabel>Fewer Follow-ups</StatLabel>
              <StatDescription>
                Clear next steps mean fewer “any update?” follow-ups
              </StatDescription>
            </StatCard>
            <StatCard>
              <StatIcon>
                <ClockCircleOutlined />
              </StatIcon>
              <StatNumber>5 min</StatNumber>
              <StatLabel>Daily Check-in</StatLabel>
              <StatDescription>
                See everything that needs attention in one place
              </StatDescription>
            </StatCard>
            <StatCard>
              <StatIcon>
                <SafetyOutlined />
              </StatIcon>
              <StatNumber>100%</StatNumber>
              <StatLabel>You Stay in Control</StatLabel>
              <StatDescription>
                Every decision goes through you. Clumse AI executes, and you stay in
                command.
              </StatDescription>
            </StatCard>
          </StatsGrid>
        </ContentContainer>
      </StatsSection>

      {/* Who This Is For / Not For Section */}
      <FitSection>
        <ContentContainer>
          <SectionHeader>
            <SectionIconWrapper>
              <UserOutlined />
            </SectionIconWrapper>
            <SectionTitle>Who This Is For</SectionTitle>
            <SectionSubtitle>
              Built for operators who want control without constant interruptions
            </SectionSubtitle>
          </SectionHeader>
          <FitGrid>
            <FitColumn>
              <FitColumnIcon>
                <CheckCircleOutlined />
              </FitColumnIcon>
              <FitColumnTitle>This is for you if…</FitColumnTitle>
              <FitList>
                <FitItem>
                  <IconWrapper $positive>
                    <HomeOutlined />
                  </IconWrapper>
                  <FitText>
                    You operate a small or mid-size portfolio (plans scale to
                    100+ doors)
                  </FitText>
                </FitItem>
                <FitItem>
                  <IconWrapper $positive>
                    <ThunderboltOutlined />
                  </IconWrapper>
                  <FitText>
                    You want fewer interruptions without losing control
                  </FitText>
                </FitItem>
                <FitItem>
                  <IconWrapper $positive>
                    <MessageOutlined />
                  </IconWrapper>
                  <FitText>
                    You're tired of maintenance requests living across texts,
                    emails, and calls
                  </FitText>
                </FitItem>
                <FitItem>
                  <IconWrapper $positive>
                    <FileTextOutlined />
                  </IconWrapper>
                  <FitText>
                    You care about setting clear expectations{'\u00A0'}early
                  </FitText>
                </FitItem>
              </FitList>
            </FitColumn>

            <FitColumn $notFor>
              <FitColumnIcon $notFor>
                <CloseCircleOutlined />
              </FitColumnIcon>
              <FitColumnTitle $notFor>This may not be a fit if…</FitColumnTitle>
              <FitList>
                <FitItem $notFor>
                  <IconWrapper>
                    <SettingOutlined />
                  </IconWrapper>
                  <FitText>You want full-service property management</FitText>
                </FitItem>
                <FitItem $notFor>
                  <IconWrapper>
                    <UserOutlined />
                  </IconWrapper>
                  <FitText>
                    You're looking for tenant marketing or listing syndication
                  </FitText>
                </FitItem>
                <FitItem $notFor>
                  <IconWrapper>
                    <ToolOutlined />
                  </IconWrapper>
                  <FitText>
                    You want to completely remove yourself from decisions
                  </FitText>
                </FitItem>
              </FitList>
            </FitColumn>
          </FitGrid>
        </ContentContainer>
      </FitSection>

      {/* What APP_NAME Actually Does */}
      <WhatSection>
        <ContentContainer>
          <SectionHeader>
            <SectionIconWrapper>
              <RocketOutlined />
            </SectionIconWrapper>
            <SectionTitle>What {APP_NAME} Actually Does</SectionTitle>
            <SectionSubtitle>
              Every subscription centers on finding and comparing bids: tickets, vendor outreach,
              bid comparison, and the records you need to approve work with
              confidence.
            </SectionSubtitle>
          </SectionHeader>
          <WhatGrid>
            <WhatCard>
              <WhatCardIcon>
                <MessageOutlined />
              </WhatCardIcon>
              <WhatCardText>
                One timeline per door so context does not get lost across channels
              </WhatCardText>
            </WhatCard>
            <WhatCard>
              <WhatCardIcon>
                <BulbOutlined />
              </WhatCardIcon>
              <WhatCardText>
                Clumse AI summarizes scope, suggests questions for vendors, and
                keeps threads readable
              </WhatCardText>
            </WhatCard>
            <WhatCard>
              <WhatCardIcon>
                <UserAddOutlined />
              </WhatCardIcon>
              <WhatCardText>
                Invite collaborators when you want a second pair of eyes on a bid
                packet
              </WhatCardText>
            </WhatCard>
            <WhatCard>
              <WhatCardIcon>
                <FileTextOutlined />
              </WhatCardIcon>
              <WhatCardText>
                Verified threads and maintenance history for your Closing Vault
              </WhatCardText>
            </WhatCard>
            <WhatCard>
              <WhatCardIcon>
                <CheckCircleOutlined />
              </WhatCardIcon>
              <WhatCardText>
                Helps set clear expectations so issues don't escalate
              </WhatCardText>
            </WhatCard>
          </WhatGrid>
          <ExplicitNote>
            <ExplicitIcon>
              <InfoCircleOutlined />
            </ExplicitIcon>
            <ExplicitContent>
              <ExplicitTitle>You stay accountable</ExplicitTitle>
              <ExplicitText>
                {APP_NAME} is software: Clumse AI accelerates drafting and
                organization; you approve vendors, spend, and timelines. Policy and
                compliance decisions stay with you.
              </ExplicitText>
            </ExplicitContent>
          </ExplicitNote>
        </ContentContainer>
      </WhatSection>

      {/* What This Replaces */}
      <ReplacesSection>
        <ContentContainer>
          <SectionHeader>
            <SectionIconWrapper>
              <ThunderboltOutlined />
            </SectionIconWrapper>
            <SectionTitle>What This Replaces</SectionTitle>
            <SectionSubtitle>
              The late-night text thread, the mystery inbox, and the spreadsheet
              maze, replaced by structured tickets, comparable bids, and a single
              place to decide what ships. Best time to introduce {APP_NAME}: when
              you are onboarding a new site or after a painful vendor cycle.
            </SectionSubtitle>
          </SectionHeader>
          <ReplacesList>
            <ReplacesItem>
              <ReplacesIcon>
                <PhoneOutlined />
              </ReplacesIcon>
              <ReplacesContent>
                <ReplacesText>Late-night "urgent" texts</ReplacesText>
                <ReplacesDescription>
                  Fake emergencies wait until morning. Real emergencies get
                  through.
                </ReplacesDescription>
              </ReplacesContent>
            </ReplacesItem>
            <ReplacesItem>
              <ReplacesIcon>
                <MailOutlined />
              </ReplacesIcon>
              <ReplacesContent>
                <ReplacesText>Weekend interruptions</ReplacesText>
                <ReplacesDescription>
                  Routine questions become Monday tasks. Your weekends are yours
                  again.
                </ReplacesDescription>
              </ReplacesContent>
            </ReplacesItem>
            <ReplacesItem>
              <ReplacesIcon>
                <MessageOutlined />
              </ReplacesIcon>
              <ReplacesContent>
                <ReplacesText>
                  "Did you see my message?" follow-ups
                </ReplacesText>
                <ReplacesDescription>
                  Tenants get instant acknowledgment. No more checking in.
                </ReplacesDescription>
              </ReplacesContent>
            </ReplacesItem>
            <ReplacesItem>
              <ReplacesIcon>
                <FileTextOutlined />
              </ReplacesIcon>
              <ReplacesContent>
                <ReplacesText>
                  Scattered spreadsheets and ad-hoc tools
                </ReplacesText>
                <ReplacesDescription>
                  Dashboards and exports meet finance where they already work: no
                  duplicate ledger required to get organized bids.
                </ReplacesDescription>
              </ReplacesContent>
            </ReplacesItem>
          </ReplacesList>
        </ContentContainer>
      </ReplacesSection>

      {/* Benefits Section */}
      <BenefitsSection>
        <ContentContainer>
          <SectionHeader>
            <SectionIconWrapper>
              <RocketOutlined />
            </SectionIconWrapper>
            <SectionTitle>Why operators choose {APP_NAME}</SectionTitle>
            <SectionSubtitle>
              Less noise in the inbox; cleaner packets going to vendors.
            </SectionSubtitle>
          </SectionHeader>
          <BenefitsGrid>
            <BenefitCard>
              <BenefitCardIcon>
                <HeartOutlined />
              </BenefitCardIcon>
              <BenefitCardTitle>Comparable bids, faster</BenefitCardTitle>
              <BenefitCardText>
                Tight scopes and side-by-side responses help you pick vendors on
                price, timing, and fit, not guesswork.
              </BenefitCardText>
            </BenefitCard>
            <BenefitCard>
              <BenefitCardIcon>
                <ThunderboltOutlined />
              </BenefitCardIcon>
              <BenefitCardTitle>Fewer Interruptions</BenefitCardTitle>
              <BenefitCardText>
                Clumse AI handles routine drafting and follow-ups. You step in when
                your decision is needed.
              </BenefitCardText>
            </BenefitCard>
            <BenefitCard>
              <BenefitCardIcon>
                <BarChartOutlined />
              </BenefitCardIcon>
              <BenefitCardTitle>Complete Context</BenefitCardTitle>
              <BenefitCardText>
                See the full history of every door and ticket. Make informed
                decisions with the facts in one thread.
              </BenefitCardText>
            </BenefitCard>
            <BenefitCard>
              <BenefitCardIcon>
                <SafetyOutlined />
              </BenefitCardIcon>
              <BenefitCardTitle>You Stay in Control</BenefitCardTitle>
              <BenefitCardText>
                Every decision goes through you. Clumse AI just handles the
                communication and organization.
              </BenefitCardText>
            </BenefitCard>
            <BenefitCard>
              <BenefitCardIcon>
                <StarOutlined />
              </BenefitCardIcon>
              <BenefitCardTitle>Better field experience</BenefitCardTitle>
              <BenefitCardText>
                Occupants and site contacts get faster acknowledgments and clearer
                next steps, without you living in SMS.
              </BenefitCardText>
            </BenefitCard>
            <BenefitCard>
              <BenefitCardIcon>
                <ClockCircleOutlined />
              </BenefitCardIcon>
              <BenefitCardTitle>5-Minute Daily Check-in</BenefitCardTitle>
              <BenefitCardText>
                See everything that needs your attention in one place. No more
                hunting through texts and emails.
              </BenefitCardText>
            </BenefitCard>
          </BenefitsGrid>

          <UseCasesBlock>
            <UseCasesSubheading>Real-world examples</UseCasesSubheading>
            <UseCasesSubcopy>How teams use {APP_NAME} day to day</UseCasesSubcopy>
          <UseCasesList>
            <UseCaseCard>
              <UseCaseNumber>01</UseCaseNumber>
              <UseCaseTitle>Urgent ticket at 11pm</UseCaseTitle>
              <UseCaseText>
                A leak hits the shared inbox. Clumse AI acknowledges immediately,
                asks clarifying questions, and opens a scoped ticket. You wake up to
                photos and constraints, not a vague text.
              </UseCaseText>
            </UseCaseCard>
            <UseCaseCard>
              <UseCaseNumber>02</UseCaseNumber>
              <UseCaseTitle>Multiple sites, one inbox</UseCaseTitle>
              <UseCaseText>
                Instead of juggling texts from eight different contacts across
                three addresses, everything lands in one organized timeline. See
                what needs attention at a glance.
              </UseCaseText>
            </UseCaseCard>
            <UseCaseCard>
              <UseCaseNumber>03</UseCaseNumber>
              <UseCaseTitle>Policy questions without ping-pong</UseCaseTitle>
              <UseCaseText>
                When someone asks about access, pets, or parking, Clumse AI drafts
                answers from your saved rules so you can approve and send once.
              </UseCaseText>
            </UseCaseCard>
            <UseCaseCard>
              <UseCaseNumber>04</UseCaseNumber>
              <UseCaseTitle>Follow-up Tracking</UseCaseTitle>
              <UseCaseText>
                "Did you call the plumber?" "When is the repair scheduled?"
                Everything is tracked in the timeline. No more mental notes or
                sticky notes.
              </UseCaseText>
            </UseCaseCard>
          </UseCasesList>
          </UseCasesBlock>
        </ContentContainer>
      </BenefitsSection>

      {/* Final CTA */}
      <CTASection>
        <CTABackgroundPattern />
        <CTAContent>
          <CTATitle>Ready for calmer vendor cycles?</CTATitle>
          <CTADescription>
            See how Clumse fits your portfolio or facilities program: no migration
            drama required to start a pilot door.
          </CTADescription>
          <CTAButton onClick={handleBookWalkthrough}>
            See How It Works
            <ArrowRightOutlined />
          </CTAButton>
          <CTASubtext>
            Quick setup • You stay in control • No commitment required
          </CTASubtext>
        </CTAContent>
      </CTASection>
    </Container>
  );
};

export default ForOperators;

// Styled Components
const Container = styled.div`
  width: 100%;
  background: var(--color-bg-primary);
`;

const HeroSection = styled.section`
  padding: 4rem 1.5rem 3.5rem;
  text-align: center;
  position: relative;
  overflow: hidden;
  background: linear-gradient(
    135deg,
    var(--color-primary-dark) 0%,
    var(--color-primary-darker) 100%
  );
  color: #ffffff;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(
      ellipse at 50% 0%,
      rgba(255, 255, 255, 0.08) 0%,
      transparent 55%
    );
    pointer-events: none;
  }

  @media ${device.mobile} {
    padding: 3.25rem 1.25rem 2.75rem;
  }
`;

const HeroBackgroundPattern = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0.05;
  background-image: radial-gradient(
      circle at 20% 30%,
      rgba(255, 255, 255, 0.3) 1px,
      transparent 1px
    ),
    radial-gradient(
      circle at 80% 70%,
      rgba(255, 255, 255, 0.3) 1px,
      transparent 1px
    );
  background-size: 60px 60px;
  pointer-events: none;
  z-index: 0;
`;

const HeroContent = styled.div`
  max-width: 40rem;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const HeroEyebrow = styled.span`
  display: block;
  font-size: 0.7rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  margin-bottom: 0.75rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.88);
  font-family: var(--font-secondary);
`;

const HeroCTA = styled.div`
  margin-top: 2rem;

  @media ${device.mobile} {
    margin-top: 1.65rem;
  }
`;

const HeroCTAButton = styled(Button)`
  background: white;
  color: var(--color-primary);
  padding: 1.2rem 2rem;
  font-size: 1.1rem;
  font-weight: 700;
  border-radius: 50px;
  border: 2px solid white;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  font-family: var(--font-secondary);
  width: auto;
  max-width: fit-content;

  .anticon {
    transition: transform 0.3s ease;
  }

  &:hover {
    background: #f7f7f7;
    transform: translateY(-2px);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.25);

    .anticon {
      transform: translateX(4px);
    }
  }

  &:active {
    transform: translateY(0);
  }

  @media ${device.mobile} {
    padding: 1rem 2rem;
    font-size: 1rem;
  }
`;

const HeroTitle = styled.h1`
  font-size: clamp(1.75rem, 4.5vw, 2.5rem);
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 1rem 0;
  font-family: var(--font-primary);
  line-height: 1.15;
  letter-spacing: -0.02em;
`;

const HeroLead = styled.p`
  font-size: 1.05rem;
  line-height: 1.6;
  margin: 0;
  color: #ffffff;
  opacity: 0.94;
  font-family: var(--font-secondary);

  @media ${device.mobile} {
    font-size: 0.98rem;
    line-height: 1.55;
  }
`;

const HeroIconWrapper = styled.div`
  width: 72px;
  height: 72px;
  background: rgba(255, 255, 255, 0.12);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.25rem;
  padding: 0.45rem;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  @media ${device.mobile} {
    width: 56px;
    height: 56px;
    margin-bottom: 1rem;
  }
`;

const FitSection = styled.section`
  padding: 6rem 2rem;
  background: linear-gradient(135deg, #f8faf9 0%, #f0f7f2 50%, #fafbfa 100%);
  position: relative;

  @media ${device.mobile} {
    padding: 4rem 1rem;
  }
`;

const ContentContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const FitGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;

  @media ${device.mobile} {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const FitColumn = styled.div`
  background: ${(props) =>
    props.$notFor
      ? "linear-gradient(135deg, var(--color-bg-primary) 0%, #f8f9fa 100%)"
      : "linear-gradient(135deg, var(--color-bg-secondary) 0%, #ffffff 100%)"};
  padding: 2.5rem 2rem;
  border-radius: 16px;
  border: 2px solid
    ${(props) =>
      props.$notFor ? "var(--color-border)" : "var(--color-primary)"};
  box-shadow: ${(props) =>
    props.$notFor
      ? "0 4px 16px rgba(0, 0, 0, 0.05)"
      : "0 4px 20px rgba(44, 95, 45, 0.1)"};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${(props) =>
      props.$notFor
        ? "linear-gradient(90deg, #94a3b8 0%, #cbd5e1 100%)"
        : "linear-gradient(90deg, var(--color-primary) 0%, var(--color-primary-dark) 100%)"};
    opacity: ${(props) => (props.$notFor ? "0.3" : "1")};
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${(props) =>
      props.$notFor
        ? "0 8px 24px rgba(0, 0, 0, 0.1)"
        : "0 12px 32px rgba(44, 95, 45, 0.2)"};
    border-color: ${(props) =>
      props.$notFor ? "var(--color-border)" : "var(--color-primary-dark)"};
  }

  @media ${device.mobile} {
    padding: 2rem 1.5rem;
  }
`;

const FitColumnIcon = styled.div`
  width: 48px;
  height: 48px;
  background: ${(props) =>
    props.$notFor ? "rgba(148, 163, 184, 0.1)" : "var(--color-primary-glow)"};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  border: 2px solid
    ${(props) =>
      props.$notFor ? "var(--color-border)" : "var(--color-primary)"};

  .anticon {
    font-size: 1.5rem;
    color: ${(props) =>
      props.$notFor ? "var(--color-text-light)" : "var(--color-primary)"};
  }

  @media ${device.mobile} {
    width: 40px;
    height: 40px;
    margin-bottom: 1rem;

    .anticon {
      font-size: 1.25rem;
    }
  }
`;

const FitColumnTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${(props) =>
    props.$notFor ? "var(--color-text-light)" : "var(--color-text)"};
  margin-bottom: 1.5rem;
  font-family: var(--font-secondary);
  text-align: center;
`;

const FitList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FitItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.25rem;
  font-family: var(--font-secondary);

  &:last-child {
    margin-bottom: 0;
  }
`;

const IconWrapper = styled.div`
  width: 32px;
  height: 32px;
  background: ${(props) =>
    props.$positive ? "var(--color-primary-glow)" : "rgba(148, 163, 184, 0.1)"};
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 0.125rem;
  border: 1px solid
    ${(props) =>
      props.$positive ? "var(--color-primary)" : "var(--color-border)"};

  .anticon {
    color: ${(props) =>
      props.$positive ? "var(--color-primary)" : "var(--color-text-light)"};
    font-size: 1rem;
  }

  @media ${device.mobile} {
    width: 28px;
    height: 28px;

    .anticon {
      font-size: 0.9rem;
    }
  }
`;

const FitText = styled.span`
  font-size: 1.1rem;
  color: ${(props) =>
    props.$notFor ? "var(--color-text-light)" : "var(--color-text)"};
  line-height: 1.6;
  flex: 1;
  text-align: left;

  @media ${device.mobile} {
    font-size: 0.95rem;
    line-height: 1.5;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const WhatSection = styled.section`
  padding: 6rem 2rem;
  background: linear-gradient(180deg, #ffffff 0%, #f9fafb 100%);
  position: relative;

  @media ${device.mobile} {
    padding: 4rem 1rem;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;

  @media ${device.mobile} {
    margin-bottom: 2rem;
  }
`;

// SectionSubtitle is defined above after SectionTitle

const SectionIconWrapper = styled.div`
  width: 64px;
  height: 64px;
  background: var(--color-primary-glow);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  border: 2px solid var(--color-primary);

  .anticon {
    font-size: 2rem;
    color: var(--color-primary);
  }

  @media ${device.mobile} {
    width: 56px;
    height: 56px;
    margin-bottom: 1rem;

    .anticon {
      font-size: 1.75rem;
    }
  }
`;

const SectionTitle = styled.h2`
  ${marketingSectionHeading}
  color: var(--color-text);
  margin-bottom: 0.5rem;
  text-align: center;

  @media ${device.mobile} {
    line-height: 1.25;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.1rem;
  color: var(--color-text-light);
  font-family: var(--font-secondary);
  text-align: center;
  line-height: 1.6;
  max-width: 38rem;
  margin: 0 auto;
  font-weight: 400;

  @media ${device.mobile} {
    font-size: 1rem;
    line-height: 1.55;
    padding: 0 0.5rem;
  }
`;

const WhatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  margin-bottom: 3rem;

  /* Fifth card: full-width row so no empty grid cell on 2-column layouts */
  & > *:nth-child(5) {
    grid-column: 1 / -1;
    max-width: 34rem;
    justify-self: center;
    width: 100%;
  }

  @media ${device.mobile} {
    grid-template-columns: 1fr;
    gap: 1rem;
    margin-bottom: 2rem;

    & > *:nth-child(5) {
      grid-column: auto;
      max-width: none;
      justify-self: stretch;
    }
  }
`;

const WhatCard = styled.div`
  background: linear-gradient(
    135deg,
    var(--color-bg-secondary) 0%,
    #ffffff 100%
  );
  padding: 2.5rem 2rem;
  border-radius: 16px;
  border: 2px solid var(--color-border-light);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.05);
  text-align: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(
      90deg,
      var(--color-primary) 0%,
      var(--color-primary-dark) 100%
    );
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 12px 32px rgba(44, 95, 45, 0.15);
    border-color: var(--color-primary);

    &::before {
      opacity: 1;
    }
  }

  @media ${device.mobile} {
    padding: 2rem 1.5rem;
  }
`;

const WhatCardIcon = styled.div`
  width: 56px;
  height: 56px;
  background: var(--color-primary-glow);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  border: 2px solid var(--color-primary);

  .anticon {
    font-size: 1.75rem;
    color: var(--color-primary);
  }

  @media ${device.mobile} {
    width: 48px;
    height: 48px;
    margin-bottom: 0.75rem;

    .anticon {
      font-size: 1.5rem;
    }
  }
`;

const WhatCardText = styled.p`
  font-size: 1.1rem;
  color: var(--color-text-secondary);
  line-height: 1.6;
  font-family: var(--font-secondary);
  margin: 0;

  @media ${device.mobile} {
    font-size: 0.95rem;
    line-height: 1.5;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const ExplicitNote = styled.div`
  background: linear-gradient(
    135deg,
    rgba(44, 95, 45, 0.06) 0%,
    var(--color-bg-secondary) 100%
  );
  padding: 1.35rem 1.5rem 1.35rem 1.25rem;
  border-radius: 14px;
  border: 1px solid rgba(44, 95, 45, 0.2);
  box-shadow: 0 2px 12px rgba(44, 95, 45, 0.06);
  max-width: 40rem;
  margin: 2.5rem auto 0;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  text-align: left;
  gap: 1rem;

  @media ${device.mobile} {
    flex-direction: row;
    padding: 1.25rem 1.15rem;
    margin-top: 1.75rem;
    gap: 0.85rem;
  }
`;

const ExplicitIcon = styled.div`
  width: 44px;
  height: 44px;
  background: var(--color-primary-glow);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 2px solid var(--color-primary);

  .anticon {
    font-size: 1.35rem;
    color: var(--color-primary);
  }

  @media ${device.mobile} {
    width: 40px;
    height: 40px;

    .anticon {
      font-size: 1.2rem;
    }
  }
`;

const ExplicitContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const ExplicitTitle = styled.h4`
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--color-text);
  margin: 0 0 0.45rem 0;
  font-family: var(--font-secondary);
  text-align: left;
  line-height: 1.3;

  @media ${device.mobile} {
    font-size: 1rem;
  }
`;

const ExplicitText = styled.p`
  font-size: 0.98rem;
  color: var(--color-text-secondary);
  margin: 0;
  font-family: var(--font-secondary);
  line-height: 1.65;
  text-align: left;

  @media ${device.mobile} {
    font-size: 0.92rem;
    line-height: 1.6;
  }
`;

const ReplacesSection = styled.section`
  padding: 6rem 2rem;
  background: linear-gradient(180deg, #f9fafb 0%, #f0f4f2 50%, #f5f7f6 100%);
  position: relative;

  @media ${device.mobile} {
    padding: 4rem 1rem;
  }
`;

const ReplacesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;

  @media ${device.mobile} {
    grid-template-columns: 1fr;
  }
`;

const ReplacesItem = styled.li`
  padding: 2rem 1.75rem;
  background: linear-gradient(
    135deg,
    var(--color-bg-primary) 0%,
    var(--color-bg-secondary) 100%
  );
  border-radius: 16px;
  border-left: 5px solid var(--color-primary);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.05);
  font-family: var(--font-secondary);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 1.25rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 5px;
    height: 100%;
    background: linear-gradient(
      180deg,
      var(--color-primary) 0%,
      var(--color-primary-dark) 100%
    );
    transition: width 0.3s ease;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 32px rgba(44, 95, 45, 0.15);
    border-left-color: var(--color-primary-dark);

    &::before {
      width: 6px;
    }
  }

  @media ${device.mobile} {
    padding: 1.5rem 1.25rem;
    gap: 1rem;
  }
`;

const ReplacesIcon = styled.div`
  width: 56px;
  height: 56px;
  background: var(--color-primary-glow);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 2px solid var(--color-primary);
  margin: 0 auto;

  .anticon {
    font-size: 1.5rem;
    color: var(--color-primary);
  }

  @media ${device.mobile} {
    width: 48px;
    height: 48px;

    .anticon {
      font-size: 1.25rem;
    }
  }
`;

const ReplacesContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: center;
  text-align: center;
  width: 100%;
`;

const ReplacesText = styled.span`
  font-size: 1.2rem;
  color: var(--color-text);
  line-height: 1.5;
  font-weight: 600;
  font-family: var(--font-secondary);
  text-align: center;

  @media ${device.mobile} {
    font-size: 1.05rem;
    line-height: 1.4;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const ReplacesDescription = styled.span`
  font-size: 1rem;
  color: var(--color-text-secondary);
  line-height: 1.5;
  font-family: var(--font-secondary);
  text-align: center;

  @media ${device.mobile} {
    font-size: 0.9rem;
    line-height: 1.4;
  }

  @media (max-width: 480px) {
    font-size: 0.85rem;
  }
`;

const CTASection = styled.section`
  background: linear-gradient(135deg, #2c5f2d 0%, #4a7c59 50%, #2c5f2d 100%);
  padding: 8rem 2rem 9rem 2rem;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(
      ellipse at 50% 50%,
      rgba(255, 255, 255, 0.1) 0%,
      rgba(44, 95, 45, 0.05) 60%,
      transparent 100%
    );
    z-index: 1;
    pointer-events: none;
  }

  @media ${device.mobile} {
    padding: 6rem 1rem 7rem 1rem;
  }
`;

const CTABackgroundPattern = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0.05;
  background-image: radial-gradient(
      circle at 20% 30%,
      rgba(255, 255, 255, 0.3) 1px,
      transparent 1px
    ),
    radial-gradient(
      circle at 80% 70%,
      rgba(255, 255, 255, 0.3) 1px,
      transparent 1px
    );
  background-size: 60px 60px;
  pointer-events: none;
  z-index: 0;
`;

const CTAContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const CTATitle = styled.h2`
  font-size: 2.75rem;
  font-weight: 700;
  color: white;
  margin-bottom: 1.5rem;
  font-family: var(--font-secondary);
  line-height: 1.2;
  letter-spacing: -0.02em;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  @media ${device.mobile} {
    font-size: 2rem;
    line-height: 1.3;
    margin-bottom: 1.25rem;
  }

  @media (max-width: 480px) {
    font-size: 1.75rem;
  }
`;

const CTADescription = styled.p`
  font-size: 1.3rem;
  color: rgba(255, 255, 255, 0.95);
  font-family: var(--font-secondary);
  line-height: 1.7;
  margin-bottom: 2.5rem;
  max-width: 700px;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);

  @media ${device.mobile} {
    font-size: 1.1rem;
    line-height: 1.6;
    margin-bottom: 2rem;
    padding: 0 0.5rem;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const CTAButton = styled(Button)`
  background: white;
  color: var(--color-primary);
  padding: 1.4rem 3.5rem;
  font-size: 1.25rem;
  font-weight: 700;
  border-radius: 50px;
  width: auto;
  min-width: 280px;
  margin-bottom: 1.75rem;
  font-family: var(--font-secondary), system-ui, ui-sans-serif, sans-serif;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2), 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 2px solid white;
  letter-spacing: -0.01em;
  position: relative;
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;

  .anticon {
    transition: transform 0.3s ease;
    font-size: 1.1rem;
  }

  &:hover {
    background: #f7f7f7;
    color: var(--color-primary-dark);
    border-color: #f7f7f7;
    transform: translateY(-3px) scale(1.02);
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.25), 0 4px 16px rgba(0, 0, 0, 0.15);

    .anticon {
      transform: translateX(4px);
    }
  }

  &:active {
    transform: translateY(-1px) scale(1);
  }

  @media ${device.mobile} {
    width: 100%;
    max-width: 100%;
    min-width: auto;
    font-size: 1.1rem;
    padding: 1.2rem 2.5rem;
    margin-bottom: 1.5rem;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
    padding: 1.1rem 2rem;
  }
`;

const CTASubtext = styled.p`
  font-size: 1.15rem;
  color: rgba(255, 255, 255, 0.9);
  font-family: var(--font-secondary), system-ui, ui-sans-serif, sans-serif;
  line-height: 1.6;
  margin: 0;
  max-width: 600px;
  text-align: center;
  letter-spacing: -0.005em;
  font-weight: 400;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);

  @media ${device.mobile} {
    font-size: 1rem;
    line-height: 1.5;
    padding: 0 0.5rem;
  }

  @media (max-width: 480px) {
    font-size: 0.95rem;
  }
`;

// New Sections
const StatsSection = styled.section`
  padding: 5rem 2rem;
  background: #ffffff;
  border-top: 2px solid #e0e0e0;
  border-bottom: 2px solid #e0e0e0;
  position: relative;

  @media ${device.mobile} {
    padding: 4rem 1rem;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;

  @media ${device.mobile} {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 1.25rem;
  }
`;

const StatCard = styled.div`
  background: linear-gradient(
    135deg,
    var(--color-bg-secondary) 0%,
    var(--color-bg-primary) 100%
  );
  padding: 2.5rem 2rem;
  border-radius: 16px;
  border: 1px solid var(--color-border-light);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.05);
  text-align: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(
      90deg,
      var(--color-primary) 0%,
      var(--color-primary-dark) 100%
    );
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 12px 32px rgba(44, 95, 45, 0.15);
    border-color: var(--color-primary);

    &::before {
      opacity: 1;
    }
  }

  @media ${device.mobile} {
    padding: 2rem 1.5rem;
  }
`;

const StatIcon = styled.div`
  width: 56px;
  height: 56px;
  background: var(--color-primary-glow);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.25rem;
  border: 2px solid var(--color-primary);

  .anticon {
    font-size: 1.75rem;
    color: var(--color-primary);
  }

  @media ${device.mobile} {
    width: 48px;
    height: 48px;
    margin-bottom: 1rem;

    .anticon {
      font-size: 1.5rem;
    }
  }
`;

const StatNumber = styled.div`
  font-size: 2.75rem;
  font-weight: 700;
  color: var(--color-primary);
  margin-bottom: 0.5rem;
  font-family: var(--font-secondary);
  line-height: 1;

  @media ${device.mobile} {
    font-size: 2.25rem;
  }
`;

const StatLabel = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 0.75rem;
  font-family: var(--font-secondary);

  @media ${device.mobile} {
    font-size: 1rem;
    margin-bottom: 0.5rem;
  }
`;

const StatDescription = styled.p`
  font-size: 0.95rem;
  color: var(--color-text-secondary);
  line-height: 1.5;
  font-family: var(--font-secondary);
  margin: 0;

  @media ${device.mobile} {
    font-size: 0.875rem;
    line-height: 1.4;
  }
`;

const BenefitsSection = styled.section`
  padding: 6rem 2rem;
  background: linear-gradient(135deg, #f0f4f2 0%, #e8f0ec 50%, #f2f7f4 100%);
  position: relative;

  @media ${device.mobile} {
    padding: 4rem 1rem;
  }
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-top: 3rem;

  @media ${device.mobile} {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    margin-top: 2rem;
  }
`;

const BenefitCard = styled.div`
  background: var(--color-bg-primary);
  padding: 2.5rem 2rem;
  border-radius: 16px;
  border: 1px solid var(--color-border-light);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.05);
  text-align: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 12px 32px rgba(44, 95, 45, 0.15);
    border-color: var(--color-primary);
  }

  @media ${device.mobile} {
    padding: 2rem 1.5rem;
  }
`;

const BenefitCardIcon = styled.div`
  width: 64px;
  height: 64px;
  background: linear-gradient(
    135deg,
    var(--color-primary-glow) 0%,
    rgba(44, 95, 45, 0.1) 100%
  );
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  border: 2px solid var(--color-primary);

  .anticon {
    font-size: 2rem;
    color: var(--color-primary);
  }

  @media ${device.mobile} {
    width: 56px;
    height: 56px;
    margin-bottom: 1.25rem;

    .anticon {
      font-size: 1.75rem;
    }
  }
`;

const BenefitCardTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--color-text);
  margin-bottom: 1rem;
  font-family: var(--font-secondary);
  line-height: 1.3;

  @media ${device.mobile} {
    font-size: 1.25rem;
    margin-bottom: 0.875rem;
  }
`;

const BenefitCardText = styled.p`
  font-size: 1.05rem;
  color: var(--color-text-secondary);
  line-height: 1.7;
  font-family: var(--font-secondary);
  margin: 0;

  @media ${device.mobile} {
    font-size: 0.95rem;
    line-height: 1.6;
  }
`;

const UseCasesBlock = styled.div`
  margin-top: 3.5rem;
  padding-top: 3rem;
  border-top: 1px solid rgba(44, 95, 45, 0.12);

  @media ${device.mobile} {
    margin-top: 2.75rem;
    padding-top: 2.25rem;
  }
`;

const UseCasesSubheading = styled.h3`
  ${marketingSectionHeading}
  font-size: clamp(1.35rem, 2.8vw, 1.85rem);
  color: var(--color-text);
  text-align: center;
  margin: 0 0 0.5rem 0;
`;

const UseCasesSubcopy = styled.p`
  font-size: 1rem;
  color: var(--color-text-light);
  font-family: var(--font-secondary);
  text-align: center;
  margin: 0 auto 2rem;
  line-height: 1.55;
  max-width: 32rem;

  @media ${device.mobile} {
    font-size: 0.95rem;
    margin-bottom: 1.5rem;
  }
`;

const UseCasesList = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin-top: 0;

  @media ${device.mobile} {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const UseCaseCard = styled.div`
  background: linear-gradient(
    135deg,
    var(--color-bg-secondary) 0%,
    var(--color-bg-primary) 100%
  );
  padding: 2.5rem;
  border-radius: 16px;
  border: 1px solid var(--color-border-light);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.05);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: linear-gradient(
      180deg,
      var(--color-primary) 0%,
      var(--color-primary-dark) 100%
    );
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 32px rgba(44, 95, 45, 0.15);
    border-color: var(--color-primary);

    &::before {
      opacity: 1;
    }
  }

  @media ${device.mobile} {
    padding: 2rem 1.5rem;
  }
`;

const UseCaseNumber = styled.div`
  font-size: 3rem;
  font-weight: 700;
  color: var(--color-primary);
  opacity: 0.2;
  font-family: var(--font-secondary);
  line-height: 1;
  margin-bottom: 1rem;

  @media ${device.mobile} {
    font-size: 2.5rem;
    margin-bottom: 0.875rem;
  }
`;

const UseCaseTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--color-text);
  margin-bottom: 1rem;
  font-family: var(--font-secondary);
  line-height: 1.3;

  @media ${device.mobile} {
    font-size: 1.25rem;
    margin-bottom: 0.875rem;
  }
`;

const UseCaseText = styled.p`
  font-size: 1.1rem;
  color: var(--color-text-secondary);
  line-height: 1.7;
  font-family: var(--font-secondary);
  margin: 0;

  @media ${device.mobile} {
    font-size: 1rem;
    line-height: 1.6;
  }
`;
